import { Component, Inject, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { RiskConvergenceService } from "../../../shared/services/risk-convergence.service";

@Component({
  selector: "app-risk-create-dialog",
  templateUrl: "./risk-create-dialog.component.html",
  styleUrls: ["./risk-create-dialog.component.scss"],
})
export class RiskCreateDialogComponent implements OnInit {
  loader: boolean = false;
  typeList: any = [];
  risksList: any = [];
  riskImpactList: any[] = [];
  riskLikelihoodList: any[] = [];
  inherentRiskScore: number | null = null;
  residualRiskScore: number | null = null;
  isEditMode: boolean = false;
  isReadOnly: boolean = false;
  selectedRisks: any[] = [];
  riskInputControl = new FormControl();
  riskFilteredOptions!: Observable<any[]>;
  riskForm = new FormGroup({
    riskId: new FormControl({ value: "", disabled: true }, [Validators.required]),
    name: new FormControl("", [Validators.required]),
    description: new FormControl("", [Validators.required]),
    type: new FormControl(null, [Validators.required]),
    date_identified: new FormControl(null),
    vulnerabilities: new FormArray([]),
    preventiveControls: new FormArray([]),
    detectiveControls: new FormArray([]),
    associatedRisks: new FormControl([]),
    inherent_risk_impact: new FormControl(null),
    inherent_risk_likelihood: new FormControl(null),
    residual_risk_impact: new FormControl(null),
    residual_risk_likelihood: new FormControl(null),
    mitigations: new FormArray([]),
  });
  riskMatrixConfig: any[] = [];

  constructor(
    public readonly dialogRef: MatDialogRef<RiskCreateDialogComponent>,
    private readonly toastrService: ToastrService,
    private readonly riskService: RiskConvergenceService,

    @Inject(MAT_DIALOG_DATA)
    public data: {
      domainIndex?: number;
      subdomainIndex?: number;
      isEditMode: boolean;
      isReadOnly?: boolean;
      riskInfo: any;
      risksList?: any;
      riskRegisterFormValue?: any;
      domainName?: string;
      subdomainName?: string;
    }
  ) { }

  ngOnInit(): void {
    this.isEditMode = !!this.data?.isEditMode;
    this.isReadOnly = !!this.data?.isReadOnly;
    this.risksList = this.data?.risksList || [];
    this.riskFilteredOptions = this.riskInputControl.valueChanges.pipe(
      startWith(""),
      map((value) => this._filterRisks(value))
    );

    this.getRiskTypes();
    this.getRiskImpactList();
    this.getRiskLikelihoodList();
    this.riskService.getRiskMatrixConfig().subscribe((config: any) => {
      if (Array.isArray(config) && config.length) {
        this.riskMatrixConfig = config;
      }
    });

    if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo) {
      this.patchFormValues();
    } else {
      this.addVulnerabilityRow("");
      this.addPreventiveControlRow("");
      this.addDetectiveControlRow("");
      this.addMitigationRow("");
    }

    if (this.isReadOnly) {
      this.riskForm.disable();
      this.riskInputControl.disable();
    }

    if (!this.isEditMode && !this.isReadOnly) {
      this.generateRiskId();
    }
  }

  createVulnerabilityRow(
    vulnerability: string = "",
    id: number | null = null,
    is_ignored: boolean = false
  ): FormGroup {
    const fg = new FormGroup({
      id: new FormControl(id),
      vulnerability: new FormControl(vulnerability),
      is_ignored: new FormControl(is_ignored),
    });
    if (this.isReadOnly) {
      fg.disable();
    }
    return fg;
  }

  get vulnerabilitiesArray(): FormArray {
    return this.riskForm.get("vulnerabilities") as FormArray;
  }

  addVulnerabilityRow(
    vulnerability: string = "",
    id: number | null = null,
    is_ignored: boolean = false
  ) {
    this.vulnerabilitiesArray.push(
      this.createVulnerabilityRow(vulnerability, id, is_ignored)
    );

    setTimeout(() => {
      const container = document.querySelector(".vulnerability-table-container");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      const newRow = document.querySelector(
        ".vulnerability-table-container tbody tr:last-child"
      );
      if (newRow) {
        newRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);
  }

  removeVulnerabilityRow(index: number) {
    this.vulnerabilitiesArray.removeAt(index);
    if (this.vulnerabilitiesArray.length === 0) {
      this.addVulnerabilityRow("");
    }
  }

  toggleIgnoreVulnerability(index: number) {
    const controlGroup = this.vulnerabilitiesArray.at(index) as FormGroup;
    if (!controlGroup) return;
    const currentVal = controlGroup.get("is_ignored")?.value;
    controlGroup.patchValue({ is_ignored: !currentVal });

    this._sortVulnerabilitiesArray();
  }

  private _sortVulnerabilitiesArray() {
    const controls = [...this.vulnerabilitiesArray.controls];
    controls.sort((a, b) => {
      const aIgnored = a.get("is_ignored")?.value ? 1 : 0;
      const bIgnored = b.get("is_ignored")?.value ? 1 : 0;
      return aIgnored - bIgnored;
    });

    this.vulnerabilitiesArray.clear();
    controls.forEach((c) => this.vulnerabilitiesArray.push(c));
  }

  getLastActiveVulnerabilityIndex(): number {
    const controls = this.vulnerabilitiesArray.controls;
    for (let i = controls.length - 1; i >= 0; i--) {
      if (!controls[i].get("is_ignored")?.value) {
        return i;
      }
    }
    return controls.length - 1;
  }

  createControlRow(description: string = "", id: number | null = null): FormGroup {
    const fg = new FormGroup({
      id: new FormControl(id),
      description: new FormControl(description),
    });
    if (this.isReadOnly) {
      fg.disable();
    }
    return fg;
  }

  get preventiveControlsArray(): FormArray {
    return this.riskForm.get("preventiveControls") as FormArray;
  }

  addPreventiveControlRow(description: string = "", id: number | null = null) {
    this.preventiveControlsArray.push(this.createControlRow(description, id));
  }

  removePreventiveControlRow(index: number) {
    this.preventiveControlsArray.removeAt(index);
    if (this.preventiveControlsArray.length === 0) {
      this.addPreventiveControlRow("");
    }
  }

  get detectiveControlsArray(): FormArray {
    return this.riskForm.get("detectiveControls") as FormArray;
  }

  addDetectiveControlRow(description: string = "", id: number | null = null) {
    this.detectiveControlsArray.push(this.createControlRow(description, id));
  }

  removeDetectiveControlRow(index: number) {
    this.detectiveControlsArray.removeAt(index);
    if (this.detectiveControlsArray.length === 0) {
      this.addDetectiveControlRow("");
    }
  }

  createMitigationRow(task: string = "", id: number | null = null): FormGroup {
    const fg = new FormGroup({
      id: new FormControl(id),
      task: new FormControl(task),
    });
    if (this.isReadOnly) {
      fg.disable();
    }
    return fg;
  }

  get mitigationsArray(): FormArray {
    return this.riskForm.get("mitigations") as FormArray;
  }

  addMitigationRow(task: string = "", id: number | null = null) {
    this.mitigationsArray.push(this.createMitigationRow(task, id));
  }

  removeMitigationRow(index: number) {
    this.mitigationsArray.removeAt(index);
    if (this.mitigationsArray.length === 0) {
      this.addMitigationRow("");
    }
  }

  private _extractId(val: any): number | null {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "object" && val !== null) {
      return val.id !== undefined && val.id !== null ? Number(val.id) : null;
    }
    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  private _getEffectiveRiskInfo(): any {
    if (!this.data?.riskInfo) return null;
    const raw = this.data.riskInfo;
    return raw && typeof raw === "object" && "data" in raw && raw.data
      ? raw.data
      : raw;
  }

  patchFormValues() {
    if (!this.data?.riskInfo) return;
    const riskInfo = this._getEffectiveRiskInfo();

    const typeId = this._extractId(riskInfo.risk_type ?? riskInfo.risk_type_id ?? riskInfo.type);
    const inherentImpactId = this._extractId(riskInfo.inherent_risk_impact ?? riskInfo.risk_impact);
    const inherentLikelihoodId = this._extractId(riskInfo.inherent_risk_likelihood ?? riskInfo.risk_likelihood);
    const residualImpactId = this._extractId(riskInfo.residual_risk_impact);
    const residualLikelihoodId = this._extractId(riskInfo.residual_risk_likelihood);

    this.riskForm.patchValue({
      riskId: riskInfo.risk_id ?? riskInfo.custom_id ?? riskInfo.code ?? "",
      name: riskInfo.name ?? riskInfo.risk_name ?? riskInfo.display_name ?? "",
      description: riskInfo.short_description ?? riskInfo.description ?? "",
      type: typeId,
      date_identified: riskInfo.date_identified
        ? (riskInfo.date_identified instanceof Date
            ? riskInfo.date_identified
            : new Date(typeof riskInfo.date_identified === "string" && !riskInfo.date_identified.includes("T")
                ? riskInfo.date_identified + "T00:00:00"
                : riskInfo.date_identified))
        : null,
      associatedRisks: riskInfo.associated_risks ?? [],
      inherent_risk_impact: inherentImpactId,
      inherent_risk_likelihood: inherentLikelihoodId,
      residual_risk_impact: residualImpactId,
      residual_risk_likelihood: residualLikelihoodId,
    });

    this.inherentRiskScore = riskInfo.inherent_risk_score ?? null;
    this.residualRiskScore = riskInfo.residual_risk_score ?? null;

    this.vulnerabilitiesArray.clear();
    const rawVulnerabilities = riskInfo.vulnerabilities || [];
    let vulnerabilitiesList: any[] = [];

    if (rawVulnerabilities.length > 0) {
      vulnerabilitiesList = rawVulnerabilities.map((v: any) => ({
        id: typeof v === "object" ? v?.id || null : null,
        vulnerability: typeof v === "string" ? v : v?.vulnerability || "",
        is_ignored: typeof v === "object" ? !!v?.is_ignored : false,
      }));
    } else if (riskInfo.vulnerability) {
      vulnerabilitiesList = [
        { id: null, vulnerability: riskInfo.vulnerability, is_ignored: false },
      ];
    }

    // Sort non-ignored first
    vulnerabilitiesList.sort((a, b) => (a.is_ignored ? 1 : 0) - (b.is_ignored ? 1 : 0));

    if (vulnerabilitiesList.length > 0) {
      vulnerabilitiesList.forEach((v) => {
        this.addVulnerabilityRow(v.vulnerability, v.id, v.is_ignored);
      });
    } else if (!this.isReadOnly) {
      this.addVulnerabilityRow("");
    }

    this.preventiveControlsArray.clear();
    this.detectiveControlsArray.clear();

    const controls = riskInfo.controls || [];
    const preventiveList = controls.filter(
      (c: any) => (c.control_type || c.type || "")?.toLowerCase() === "preventive"
    );
    const detectiveList = controls.filter(
      (c: any) => (c.control_type || c.type || "")?.toLowerCase() === "detective"
    );

    if (preventiveList.length > 0) {
      preventiveList.forEach((c: any) => {
        this.addPreventiveControlRow(c.description || c.name || "", c.id || null);
      });
    } else if (!this.isReadOnly) {
      this.addPreventiveControlRow("");
    }

    if (detectiveList.length > 0) {
      detectiveList.forEach((c: any) => {
        this.addDetectiveControlRow(c.description || c.name || "", c.id || null);
      });
    } else if (!this.isReadOnly) {
      this.addDetectiveControlRow("");
    }

    this.inherentRiskScore = riskInfo.inherent_risk_score ?? null;
    this.residualRiskScore = riskInfo.residual_risk_score ?? null;

    this.mitigationsArray.clear();
    const mitigations = riskInfo.mitigations || [];
    if (mitigations.length > 0) {
      mitigations.forEach((mit: any) => {
        const taskText = typeof mit === "string" ? mit : mit?.action_tasks || mit?.task || "";
        const mitId = typeof mit === "object" && mit !== null ? mit?.id || null : null;
        this.addMitigationRow(taskText, mitId);
      });
    } else if (!this.isReadOnly) {
      this.addMitigationRow("");
    }

    const associatedRiskIds = riskInfo.associated_risks || [];
    this.selectedRisks = (this.risksList || []).filter((risk: any) =>
      associatedRiskIds.includes(risk.id)
    );
    if (this.selectedRisks.length === 0 && Array.isArray(riskInfo.associated_risks) && riskInfo.associated_risks.length > 0) {
      if (typeof riskInfo.associated_risks[0] === 'object') {
        this.selectedRisks = riskInfo.associated_risks;
      }
    }

    if (this.isReadOnly) {
      this.riskForm.disable();
      this.riskInputControl.disable();
    }
  }

  getRiskTypes() {
    this.riskService.getRiskTypes().subscribe((res: any) => {
      this.typeList = res || [];
      if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo) {
        const riskInfo = this._getEffectiveRiskInfo();
        const typeId = this._extractId(riskInfo?.risk_type ?? riskInfo?.risk_type_id ?? riskInfo?.type);
        if (typeId) {
          this.riskForm.patchValue({ type: typeId });
        }
      }
      if (this.isReadOnly) {
        this.riskForm.disable();
      }
    });
  }

  getRiskImpactList() {
    this.riskService.getRiskImpactList().subscribe((res: any) => {
      this.riskImpactList = res || [];
      if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo) {
        const riskInfo = this._getEffectiveRiskInfo();
        const inherentImpactId = this._extractId(riskInfo?.inherent_risk_impact ?? riskInfo?.risk_impact);
        const residualImpactId = this._extractId(riskInfo?.residual_risk_impact);

        if (inherentImpactId) {
          this.riskForm.patchValue({ inherent_risk_impact: inherentImpactId });
        }
        if (residualImpactId) {
          this.riskForm.patchValue({ residual_risk_impact: residualImpactId });
        }
      }
      this.calculateInherentRiskScore();
      this.calculateResidualRiskScore();
      if (this.isReadOnly) {
        this.riskForm.disable();
      }
    });
  }

  getRiskLikelihoodList() {
    this.riskService.getRiskLikelihoodList().subscribe((res: any) => {
      this.riskLikelihoodList = res || [];
      if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo) {
        const riskInfo = this._getEffectiveRiskInfo();
        const inherentLikelihoodId = this._extractId(riskInfo?.inherent_risk_likelihood ?? riskInfo?.risk_likelihood);
        const residualLikelihoodId = this._extractId(riskInfo?.residual_risk_likelihood);

        if (inherentLikelihoodId) {
          this.riskForm.patchValue({ inherent_risk_likelihood: inherentLikelihoodId });
        }
        if (residualLikelihoodId) {
          this.riskForm.patchValue({ residual_risk_likelihood: residualLikelihoodId });
        }
      }
      this.calculateInherentRiskScore();
      this.calculateResidualRiskScore();
      if (this.isReadOnly) {
        this.riskForm.disable();
      }
    });
  }

  getDisplayRiskId(): string {
    const risk = this._getEffectiveRiskInfo();
    return (
      risk?.risk_id ||
      risk?.custom_id ||
      risk?.code ||
      this.riskForm.get("riskId")?.value ||
      ""
    );
  }

  getDisplayRiskName(): string {
    const risk = this._getEffectiveRiskInfo();
    return (
      risk?.name ||
      risk?.display_name ||
      risk?.risk_name ||
      this.riskForm.get("name")?.value ||
      ""
    );
  }

  getDisplayRiskDescription(): string {
    const risk = this._getEffectiveRiskInfo();
    return (
      risk?.description ||
      risk?.short_description ||
      this.riskForm.get("description")?.value ||
      ""
    );
  }

  getDisplayRiskType(): string {
    const risk = this._getEffectiveRiskInfo();
    if (risk) {
      if (typeof risk.risk_type === "object" && risk.risk_type !== null) {
        return risk.risk_type.display_name || risk.risk_type.name || "";
      }
      if (typeof risk.type === "object" && risk.type !== null) {
        return risk.type.display_name || risk.type.name || "";
      }
      if (typeof risk.risk_type === "string" && isNaN(Number(risk.risk_type))) {
        return risk.risk_type;
      }
      if (typeof risk.risk_type_name === "string") return risk.risk_type_name;
      if (typeof risk.type_name === "string") return risk.type_name;
    }
    const typeId = this._extractId(
      risk?.risk_type ?? risk?.risk_type_id ?? risk?.type ?? this.riskForm.get("type")?.value
    );
    if (typeId && this.typeList?.length) {
      const found = this.typeList.find((t: any) => Number(t.id) === typeId);
      if (found) return found.display_name || found.name || "";
    }
    return "";
  }

  getActiveVulnerabilities(): { vulnerability: string; is_ignored: boolean }[] {
    const list: { vulnerability: string; is_ignored: boolean }[] = [];
    if (this.vulnerabilitiesArray?.controls?.length > 0) {
      for (const ctrl of this.vulnerabilitiesArray.controls) {
        const text = ctrl.get("vulnerability")?.value?.trim();
        if (text) {
          list.push({
            vulnerability: text,
            is_ignored: !!ctrl.get("is_ignored")?.value,
          });
        }
      }
    }
    if (list.length === 0) {
      const risk = this._getEffectiveRiskInfo();
      const raw = risk?.vulnerabilities || [];
      if (raw.length > 0) {
        raw.forEach((v: any) => {
          const text = typeof v === "string" ? v.trim() : v?.vulnerability?.trim();
          if (text) {
            list.push({
              vulnerability: text,
              is_ignored: typeof v === "object" ? !!v?.is_ignored : false,
            });
          }
        });
      } else if (risk?.vulnerability) {
        list.push({ vulnerability: risk.vulnerability.trim(), is_ignored: false });
      }
    }
    return list;
  }

  getPreventiveControlsList(): string[] {
    const list: string[] = [];
    if (this.preventiveControlsArray?.controls?.length > 0) {
      for (const ctrl of this.preventiveControlsArray.controls) {
        const text = ctrl.get("description")?.value?.trim();
        if (text) list.push(text);
      }
    }
    if (list.length === 0) {
      const risk = this._getEffectiveRiskInfo();
      const controls = risk?.controls || [];
      controls
        .filter((c: any) => (c.control_type || c.type || "")?.toLowerCase() === "preventive")
        .forEach((c: any) => {
          const text = (c.description || c.name || "").trim();
          if (text) list.push(text);
        });
    }
    return list;
  }

  getDetectiveControlsList(): string[] {
    const list: string[] = [];
    if (this.detectiveControlsArray?.controls?.length > 0) {
      for (const ctrl of this.detectiveControlsArray.controls) {
        const text = ctrl.get("description")?.value?.trim();
        if (text) list.push(text);
      }
    }
    if (list.length === 0) {
      const risk = this._getEffectiveRiskInfo();
      const controls = risk?.controls || [];
      controls
        .filter((c: any) => (c.control_type || c.type || "")?.toLowerCase() === "detective")
        .forEach((c: any) => {
          const text = (c.description || c.name || "").trim();
          if (text) list.push(text);
        });
    }
    return list;
  }

  getMitigationTasksList(): string[] {
    const list: string[] = [];
    if (this.mitigationsArray?.controls?.length > 0) {
      for (const ctrl of this.mitigationsArray.controls) {
        const text = ctrl.get("task")?.value?.trim();
        if (text) list.push(text);
      }
    }
    if (list.length === 0) {
      const risk = this._getEffectiveRiskInfo();
      const mitigations = risk?.mitigations || [];
      mitigations.forEach((m: any) => {
        const text = typeof m === "string" ? m.trim() : (m?.action_tasks || m?.task || "").trim();
        if (text) list.push(text);
      });
    }
    return list;
  }

  getInherentImpactName(): string {
    const risk = this._getEffectiveRiskInfo();
    if (typeof risk?.inherent_risk_impact === "object" && risk.inherent_risk_impact?.display_name) {
      return risk.inherent_risk_impact.display_name;
    }
    const impactId = this._extractId(this.riskForm.get("inherent_risk_impact")?.value ?? risk?.inherent_risk_impact ?? risk?.risk_impact);
    if (impactId && this.riskImpactList?.length) {
      const found = this.riskImpactList.find((i: any) => Number(i.id) === impactId);
      if (found) return found.display_name || found.name || "";
    }
    if (typeof risk?.inherent_risk_impact === "string" && isNaN(Number(risk.inherent_risk_impact))) return risk.inherent_risk_impact;
    if (typeof risk?.inherent_impact_name === "string") return risk.inherent_impact_name;
    return "";
  }

  getInherentLikelihoodName(): string {
    const risk = this._getEffectiveRiskInfo();
    if (typeof risk?.inherent_risk_likelihood === "object" && risk.inherent_risk_likelihood?.display_name) {
      return risk.inherent_risk_likelihood.display_name;
    }
    const likelihoodId = this._extractId(this.riskForm.get("inherent_risk_likelihood")?.value ?? risk?.inherent_risk_likelihood ?? risk?.risk_likelihood);
    if (likelihoodId && this.riskLikelihoodList?.length) {
      const found = this.riskLikelihoodList.find((l: any) => Number(l.id) === likelihoodId);
      if (found) return found.display_name || found.name || "";
    }
    if (typeof risk?.inherent_risk_likelihood === "string" && isNaN(Number(risk.inherent_risk_likelihood))) return risk.inherent_risk_likelihood;
    if (typeof risk?.inherent_likelihood_name === "string") return risk.inherent_likelihood_name;
    return "";
  }

  getResidualImpactName(): string {
    const risk = this._getEffectiveRiskInfo();
    if (typeof risk?.residual_risk_impact === "object" && risk.residual_risk_impact?.display_name) {
      return risk.residual_risk_impact.display_name;
    }
    const impactId = this._extractId(this.riskForm.get("residual_risk_impact")?.value ?? risk?.residual_risk_impact);
    if (impactId && this.riskImpactList?.length) {
      const found = this.riskImpactList.find((i: any) => Number(i.id) === impactId);
      if (found) return found.display_name || found.name || "";
    }
    if (typeof risk?.residual_risk_impact === "string" && isNaN(Number(risk.residual_risk_impact))) return risk.residual_risk_impact;
    if (typeof risk?.residual_impact_name === "string") return risk.residual_impact_name;
    return "";
  }

  getResidualLikelihoodName(): string {
    const risk = this._getEffectiveRiskInfo();
    if (typeof risk?.residual_risk_likelihood === "object" && risk.residual_risk_likelihood?.display_name) {
      return risk.residual_risk_likelihood.display_name;
    }
    const likelihoodId = this._extractId(this.riskForm.get("residual_risk_likelihood")?.value ?? risk?.residual_risk_likelihood);
    if (likelihoodId && this.riskLikelihoodList?.length) {
      const found = this.riskLikelihoodList.find((l: any) => Number(l.id) === likelihoodId);
      if (found) return found.display_name || found.name || "";
    }
    if (typeof risk?.residual_risk_likelihood === "string" && isNaN(Number(risk.residual_risk_likelihood))) return risk.residual_risk_likelihood;
    if (typeof risk?.residual_likelihood_name === "string") return risk.residual_likelihood_name;
    return "";
  }

  get dialogTitle(): string {
    if (this.isReadOnly) {
      return "Risk Info";
    }
    return this.isEditMode ? "Edit Risk" : "New Risk";
  }

  get domainName(): string {
    if (this.data?.domainName) {
      return this.data.domainName;
    }
    if (this.data?.riskInfo?.risk_domain?.name || this.data?.riskInfo?.domain_name) {
      return this.data.riskInfo.risk_domain?.name || this.data.riskInfo.domain_name;
    }
    if (this.data?.riskRegisterFormValue && this.data?.domainIndex !== undefined) {
      const domain = this.data.riskRegisterFormValue[this.data.domainIndex];
      return domain?.display_name || domain?.name || "";
    }
    return "";
  }

  get subdomainName(): string {
    if (this.data?.subdomainName) {
      return this.data.subdomainName;
    }
    if (this.data?.riskInfo?.risk_sub_domain?.name || this.data?.riskInfo?.subdomain_name) {
      return this.data.riskInfo.risk_sub_domain?.name || this.data.riskInfo.subdomain_name;
    }
    if (
      this.data?.riskRegisterFormValue &&
      this.data?.domainIndex !== undefined &&
      this.data?.subdomainIndex !== undefined
    ) {
      const domain = this.data.riskRegisterFormValue[this.data.domainIndex];
      const subdomain = domain?.subdomains?.[this.data.subdomainIndex];
      return subdomain?.display_name || subdomain?.name || "";
    }
    return "";
  }

  generateRiskId() {
    const domainIndex = this.data.domainIndex;
    if (domainIndex === undefined || !this.data?.riskRegisterFormValue) return;
    const originalDomain = this.data.riskRegisterFormValue[domainIndex];
    if (!originalDomain) return;

    const domainName = originalDomain.display_name || originalDomain.name || "";

    const words = domainName.trim().split(/\s+/);
    let domainAbbr = "";
    if (words.length > 1) {
      domainAbbr = words.map((w: string) => w[0].toUpperCase()).join("");
    } else if (words.length === 1 && words[0].length > 0) {
      domainAbbr = words[0].substring(0, 2).toUpperCase();
    }

    let totalRisks = 0;
    if (originalDomain.subdomains) {
      originalDomain.subdomains.forEach((sub: any) => {
        totalRisks += (sub.risks?.length || 0);
      });
    }
    const nextVal = totalRisks + 1;
    const nextValStr = nextVal.toString().padStart(3, "0");

    this.riskService.getRiskPrefix().subscribe((res: any) => {
      const prefix = res?.value || "";
      const finalRiskId = `${prefix}-${domainAbbr}${nextValStr}`;
      this.riskForm.patchValue({ riskId: finalRiskId });
    });
  }

  onInherentImpactChange() {
    this.calculateInherentRiskScore();
  }

  onInherentLikelihoodChange() {
    this.calculateInherentRiskScore();
  }

  calculateInherentRiskScore() {
    const impactId = this._extractId(this.riskForm.get("inherent_risk_impact")?.value);
    const likelihoodId = this._extractId(this.riskForm.get("inherent_risk_likelihood")?.value);
    const selectedImpact = this.riskImpactList.find((i: any) => Number(i.id) === impactId);
    const selectedLikelihood = this.riskLikelihoodList.find((l: any) => Number(l.id) === likelihoodId);

    if (selectedImpact && selectedLikelihood) {
      const impactScore = Number(selectedImpact.name || selectedImpact.id);
      const likelihoodScore = Number(selectedLikelihood.name || selectedLikelihood.id);
      this.inherentRiskScore = impactScore * likelihoodScore;
    } else if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo?.inherent_risk_score != null) {
      this.inherentRiskScore = this.data.riskInfo.inherent_risk_score;
    }
  }

  onResidualImpactChange() {
    this.calculateResidualRiskScore();
  }

  onResidualLikelihoodChange() {
    this.calculateResidualRiskScore();
  }

  calculateResidualRiskScore() {
    const impactId = this._extractId(this.riskForm.get("residual_risk_impact")?.value);
    const likelihoodId = this._extractId(this.riskForm.get("residual_risk_likelihood")?.value);
    const selectedImpact = this.riskImpactList.find((i: any) => Number(i.id) === impactId);
    const selectedLikelihood = this.riskLikelihoodList.find((l: any) => Number(l.id) === likelihoodId);

    if (selectedImpact && selectedLikelihood) {
      const impactScore = Number(selectedImpact.name || selectedImpact.id);
      const likelihoodScore = Number(selectedLikelihood.name || selectedLikelihood.id);
      this.residualRiskScore = impactScore * likelihoodScore;
    } else if ((this.isEditMode || this.isReadOnly) && this.data.riskInfo?.residual_risk_score != null) {
      this.residualRiskScore = this.data.riskInfo.residual_risk_score;
    }
  }

  getInherentRiskScoreColor(): string {
    return this.riskService.getRiskScoreColorFromConfig(this.inherentRiskScore, this.riskMatrixConfig);
  }

  getResidualRiskScoreColor(): string {
    return this.riskService.getRiskScoreColorFromConfig(this.residualRiskScore, this.riskMatrixConfig);
  }

  getPayload() {
    const formValues = this.riskForm.getRawValue();

    const controls: any[] = [];

    const preventiveVal = this.preventiveControlsArray.getRawValue();
    (preventiveVal || []).forEach((c: any) => {
      const desc = typeof c === "string" ? c.trim() : c?.description?.trim();
      if (desc) {
        const item: any = {
          control_type: "preventive",
          description: desc,
        };
        if (c && typeof c === "object" && c.id) {
          item.id = c.id;
        }
        controls.push(item);
      }
    });

    const detectiveVal = this.detectiveControlsArray.getRawValue();
    (detectiveVal || []).forEach((c: any) => {
      const desc = typeof c === "string" ? c.trim() : c?.description?.trim();
      if (desc) {
        const item: any = {
          control_type: "detective",
          description: desc,
        };
        if (c && typeof c === "object" && c.id) {
          item.id = c.id;
        }
        controls.push(item);
      }
    });

    const vulnerabilitiesPayload: any[] = [];
    const vulnerabilitiesVal = this.vulnerabilitiesArray.getRawValue();
    (vulnerabilitiesVal || []).forEach((v: any) => {
      const textStr = typeof v === "string" ? v.trim() : v?.vulnerability?.trim();
      if (textStr) {
        const item: any = {
          vulnerability: textStr,
          is_ignored: typeof v === "object" ? !!v.is_ignored : false,
        };
        if (v && typeof v === "object" && v.id) {
          item.id = v.id;
        }
        vulnerabilitiesPayload.push(item);
      }
    });

    const mitigationsPayload: any[] = [];
    const mitigationsVal = this.mitigationsArray.getRawValue();
    (mitigationsVal || []).forEach((m: any) => {
      const taskStr = typeof m === "string" ? m.trim() : m?.task?.trim();
      if (taskStr) {
        const item: any = { task: taskStr };
        if (m && typeof m === "object" && m.id) {
          item.id = m.id;
        }
        mitigationsPayload.push(item);
      }
    });

    const rawDate = formValues.date_identified;
    let formattedDateIdentified: string | null = null;
    if (rawDate) {
      if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
        formattedDateIdentified = rawDate;
      } else {
        const d = new Date(rawDate);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          formattedDateIdentified = `${year}-${month}-${day}`;
        }
      }
    }

    const riskPayload = {
      id: this.isEditMode ? this.data.riskInfo.id : undefined,
      risk_id: formValues.riskId,
      name: formValues.name,
      prefix: "",
      impact: "",
      short_description: formValues.description,
      date_identified: formattedDateIdentified,
      vulnerability: vulnerabilitiesPayload[0]?.vulnerability || null,
      vulnerabilities: vulnerabilitiesPayload,
      controls,
      mitigations: mitigationsPayload,
      risk_type_id: formValues.type,
      associated_risks: this.selectedRisks.map((r) => r.id),
      inherent_risk_impact: formValues.inherent_risk_impact || null,
      inherent_risk_likelihood: formValues.inherent_risk_likelihood || null,
      inherent_risk_score: this.inherentRiskScore,
      residual_risk_impact: formValues.residual_risk_impact || null,
      residual_risk_likelihood: formValues.residual_risk_likelihood || null,
      residual_risk_score: this.residualRiskScore,
    };

    const domainIndex = this.data.domainIndex ?? 0;
    const subdomainIndex = this.data.subdomainIndex ?? 0;

    const originalDomain = this.data.riskRegisterFormValue?.[domainIndex] || {};
    const originalSubdomain = originalDomain?.subdomains?.[subdomainIndex] || {};

    return {
      domains: [
        {
          id: originalDomain.id,
          uuid: originalDomain.uuid,
          display_name: originalDomain.display_name,
          name: originalDomain.name,
          subdomains: [
            {
              id: originalSubdomain.id,
              uuid: originalSubdomain.uuid,
              display_name: originalSubdomain.display_name,
              name: originalSubdomain.name,
              risk_domain: originalSubdomain.risk_domain,
              risks: [riskPayload],
            },
          ],
        },
      ],
      deleted_domains: [],
      deleted_subdomains: [],
      deleted_risks: [],
    };
  }

  save() {
    this.riskForm.markAllAsTouched();
    if (this.riskForm.invalid) return;
    const payload = this.getPayload();
    this.loader = true;

    this.riskService.updateRisk(payload).subscribe(
      () => {
        this.toastrService.success("Risk updated");
        this.loader = false;
        this.dialogRef.close(true);
      },
      (error) => {
        this.loader = false;
        throw error;
      }
    );
  }

  closeModal(): void {
    this.dialogRef.close(false);
  }

  private _filterRisks(value: any): any[] {
    const filterValue =
      typeof value === "string"
        ? value.toLowerCase()
        : value?.display_name?.toLowerCase() || "";

    return (
      this.risksList
        // remove current risk that is being updated
        .filter((risk: any) => {
          if (!this.isEditMode) return true;
          return risk.id !== this.data?.riskInfo?.id;
        })

        // remove already selected risks
        .filter(
          (risk: any) =>
            !this.selectedRisks.some((selected) => selected.id === risk.id)
        )

        // apply search filter
        .filter((risk: any) =>
          risk.display_name.toLowerCase().includes(filterValue)
        )
    );
  }

  addRisk(risk: any) {
    if (!this.selectedRisks.find((r) => r.id === risk.id)) {
      this.selectedRisks.push(risk);
    }
    this.riskInputControl.setValue("");
  }

  removeRisk(index: number) {
    this.selectedRisks.splice(index, 1);
    this.riskInputControl.setValue("");
  }

  displayRisk(risk: any): string {
    if (!risk) return "";
    if (typeof risk === "string" || typeof risk === "number") return String(risk);
    const name = risk.display_name || risk.name || risk.risk_name || "";
    return risk.risk_id ? `${risk.risk_id} - ${name}` : (name || String(risk.id || ""));
  }
}
